/**
 * What the work is called, once, for the whole app.
 *
 * The API owns this list (api/app/steps.py, served at /meta/work-vocabulary) and a
 * test asserts the two agree. Colours live here because they are a UI concern, keyed
 * by the same step keys so a renamed step cannot end up a different colour on the
 * calendar than in a filter.
 *
 *   STEPS        the steps a creative moves through, in order
 *   STAGE_LABEL  the coarse stage names, for the places where a stage is the subject
 */

export interface StepDef { key: string; label: string; stage: string; color: string }

export const STEPS: StepDef[] = [
  { key: 'script',  label: 'Script',          stage: 'brief',       color: 'bg-slate-700/40 text-slate-300 border-slate-600/40' },
  { key: 'doctor',  label: 'Doctor approval', stage: 'brief',       color: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  { key: 'shoot',   label: 'Shoot',           stage: 'shoot',       color: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  { key: 'raws',    label: 'Raws upload',     stage: 'shoot',       color: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  { key: 'ad_raws', label: 'AD reviews raws', stage: 'raws_review', color: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
  { key: 'rough',   label: 'Rough cut',       stage: 'edit',        color: 'bg-violet-500/15 text-violet-300 border-violet-500/30' },
  { key: 'colour',  label: 'Colouring',       stage: 'edit',        color: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
  { key: 'final',   label: 'Final edit',      stage: 'edit',        color: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30' },
  { key: 'dual',    label: 'Final edit approval', stage: 'dual_review', color: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
  // the posting person checks the approved cut before the clinic (Aziz 2026-11-04)
  { key: 'final_rev', label: 'Final revision', stage: 'dual_review', color: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  { key: 'client',  label: 'Client review',   stage: 'done',        color: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
  { key: 'posted',  label: 'Posted',          stage: 'posted',      color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
];

export const STEP_LABEL: Record<string, string> = Object.fromEntries(STEPS.map((s) => [s.key, s.label]));
export const STEP_COLOR: Record<string, string> = Object.fromEntries(STEPS.map((s) => [s.key, s.color]));
export const STEP_OF_STAGE: Record<string, string> = {
  brief: 'script', shoot: 'shoot', raws_review: 'ad_raws', edit: 'rough',
  dual_review: 'dual', done: 'client', posted: 'posted',
};

/** The next-action keys the tracker returns, folded onto the step they belong to.
 *  Mirrors NEXT_TO_STEP in api/app/steps.py. */
export const NEXT_TO_STEP: Record<string, string> = {
  script: 'script',
  doctor_send: 'doctor', doctor: 'doctor', doctor_fix: 'doctor', doctor_link: 'doctor',
  shoot_plan: 'shoot', shoot: 'shoot',
  raws: 'raws',
  ad_raws: 'ad_raws',
  edit: 'rough', design: 'rough', rough: 'rough',
  colour: 'colour',
  final: 'final',
  dual: 'dual', dual_fix: 'dual',
  final_rev: 'final_rev', final_rev_fix: 'final_rev',
  client_send: 'client', client: 'client', client_fix: 'client',
  post: 'posted', done: 'posted',
};

export function stepOfNext(nextKey?: string | null): string {
  if (!nextKey) return 'posted';
  return NEXT_TO_STEP[nextKey] ?? nextKey;
}

/**
 * The latest stage at which a step can still honestly be the next thing to do.
 * Mirrors STEP_FLOOR in api/app/steps.py. Not the same as the stage a step belongs
 * to: submitting a reel parks it on Shoot while the script is still ahead of it.
 */
export const STEP_FLOOR: Record<string, string> = {
  script: 'shoot',
  doctor: 'shoot',
  shoot: 'shoot',
  raws: 'shoot',
  ad_raws: 'raws_review',
  rough: 'edit',
  colour: 'edit',
  final: 'edit',
  dual: 'dual_review',
  final_rev: 'dual_review',
  client: 'done',
  posted: 'posted',
};

/** Steps that cannot happen for a creative type are not offered as a filter. */
export const STEPS_BY_TYPE: Record<string, string[]> = {
  video: STEPS.map((s) => s.key),
  reel: STEPS.map((s) => s.key),
  photo: STEPS.map((s) => s.key),
  static: STEPS.filter((s) => !['shoot', 'raws', 'ad_raws'].includes(s.key)).map((s) => s.key),
  carousel: STEPS.filter((s) => !['shoot', 'raws', 'ad_raws'].includes(s.key)).map((s) => s.key),
  story: STEPS.filter((s) => !['shoot', 'raws', 'ad_raws'].includes(s.key)).map((s) => s.key),
  copy: STEPS.filter((s) => !['shoot', 'raws', 'ad_raws'].includes(s.key)).map((s) => s.key),
};

export function stepsForType(creativeType?: string | null): StepDef[] {
  const keys = STEPS_BY_TYPE[creativeType ?? 'video'] ?? STEPS.map((s) => s.key);
  return STEPS.filter((s) => keys.includes(s.key));
}

/** The coarse stages. Use these only where a stage really is the subject: the SLA
 *  clocks, the dashboard funnel. Anything people read as "where is this" uses STEPS. */
export const STAGE_LABEL: Record<string, string> = {
  brief: 'Brief',
  shoot: 'Shoot',
  raws_review: 'AD Review',
  edit: 'Edit',
  dual_review: 'Final edit approval',
  done: 'Done',
  posted: 'Posted',
};
export const STAGE_ORDER: string[] = Object.keys(STAGE_LABEL);


/** The three edit phases, in order, and what each is called per family of work. Mirrors
 *  PHASE_NAMES in api/app/steps.py (tests/one-source-tests.py checks the two agree). */
export const PHASES = ['rough', 'colour', 'final'] as const;
export type Phase = typeof PHASES[number];
export const PHASE_NAMES: Record<'video' | 'photo' | 'static', Record<Phase, string>> = {
  video:  { rough: 'Rough cut', colour: 'Colouring', final: 'Inserts and final edit' },
  photo:  { rough: 'Selection', colour: 'Retouch and colour', final: 'Retouch' },
  static: { rough: 'Draft design', colour: 'Refinement', final: 'Design' },
};
/** A static, carousel or story: one design step (Aziz, 2026-10-04). Mirrors steps.is_design. */
export const isDesign = (creativeType?: string | null) => familyOf(creativeType) === 'static';
/** One edit step in rounds: the static family and, since 2026-10-30, photo. Mirrors upload_rules.single_edit. */
export const singleEdit = (creativeType?: string | null) => familyOf(creativeType) !== 'video';
/** The edit steps this kind of work goes through: video goes rough cut, colouring (the art
 *  director's stage ends there), then inserts and final edit, which the content creator
 *  approves (Aziz 2026-10-30). Statics and photo have one step in rounds. */
export const phasesFor = (creativeType?: string | null): readonly Phase[] =>
  (singleEdit(creativeType) ? (['final'] as const) : PHASES);
/** video (video, reel, unknown), photo, or static (everything designed); steps.family_of */
export function familyOf(creativeType?: string | null): 'video' | 'photo' | 'static' {
  if (creativeType === 'photo') return 'photo';
  return !creativeType || creativeType === 'video' || creativeType === 'reel' ? 'video' : 'static';
}
/** The phase names for this creative type. */
export function phaseNames(creativeType?: string | null): Record<Phase, string> {
  return PHASE_NAMES[familyOf(creativeType)];
}
